import { useTranslation } from "react-i18next";
import { DocumentText1, DocumentText, Calendar, WalletMoney, Building, Clock, CardPos } from "iconsax-react";
import cn from "classnames";
import { ModalForm, Badge, Skeleton } from "@/shared/ui";
import { CheckIcon } from "@/shared/assets/icons";
import { useScrollDetection } from "@/shared/hooks";
import { formatDateForDisplay } from "@/shared/utils";
import { formatMoneyKzt } from "@/features/accounting/shared";
import { useGetJournalEntryDetailQuery } from "../../../api";

interface Props {
  entryId: number;
  onClose: () => void;
}

const SKELETON_ROWS = [
  { labelWidth: 120, valueWidth: 100 },
  { labelWidth: 110, valueWidth: 180 },
  { labelWidth: 110, valueWidth: 180 },
  { labelWidth: 80, valueWidth: 140 },
  { labelWidth: 100, valueWidth: 220 },
  { labelWidth: 120, valueWidth: 100 },
  { labelWidth: 90, valueWidth: 60 },
  { labelWidth: 80, valueWidth: 80 },
  { labelWidth: 110, valueWidth: 160 },
  { labelWidth: 120, valueWidth: 130 },
];

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-6 h-full min-h-0">
      <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke shrink-0">
        <Skeleton height={28} width={240} />
      </div>
      <div className="flex-1 overflow-auto min-h-0">
        <div className="flex flex-col">
          {SKELETON_ROWS.map(function renderSkeletonRow(row, idx) {
            return (
              <div
                key={idx}
                className={cn(
                  "py-3 flex items-center gap-3",
                  idx < SKELETON_ROWS.length - 1 && "border-b surface-base-stroke",
                )}>
                <Skeleton height={16} width={16} borderRadius={4} />
                <Skeleton height={16} width={row.labelWidth} />
                <div className="flex-1" />
                <Skeleton height={16} width={row.valueWidth} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function JournalEntryDetailContent({
  entry,
  currentLang,
  scrollRef,
  hasScroll,
  t,
}: {
  entry: NonNullable<ReturnType<typeof useGetJournalEntryDetailQuery>["data"]>;
  currentLang: "ru" | "kk" | "en";
  scrollRef: ReturnType<typeof useScrollDetection>["scrollRef"];
  hasScroll: boolean;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  const descriptionText =
    entry.description[currentLang] || entry.description.kk || entry.description.en || entry.description.ru;

  const statusConfig: Record<
    string,
    { color: "info" | "positive"; variant: "soft" | "outline"; icon: React.ReactNode }
  > = {
    draft: {
      color: "info",
      variant: "soft",
      icon: <DocumentText size={16} color="currentColor" />,
    },
    created: {
      color: "info",
      variant: "soft",
      icon: <DocumentText size={16} color="currentColor" />,
    },
    approved: {
      color: "info",
      variant: "soft",
      icon: <CardPos size={16} color="currentColor" />,
    },
    paid: {
      color: "positive",
      variant: "soft",
      icon: <CheckIcon />,
    },
  };

  const statusStyle = statusConfig[entry.status] || statusConfig.created;

  const fields = [
    {
      key: "entryDate",
      label: t("journalEntries.detail.entryDate"),
      value: formatDateForDisplay(entry.entry_date, false),
      icon: Calendar,
    },
    {
      key: "debitAccount",
      label: t("journalEntries.detail.debitAccount"),
      value: `${entry.debit_account} — ${entry.account_names.debit}`,
      icon: WalletMoney,
    },
    {
      key: "creditAccount",
      label: t("journalEntries.detail.creditAccount"),
      value: `${entry.credit_account} — ${entry.account_names.credit}`,
      icon: WalletMoney,
    },
    {
      key: "amount",
      label: t("journalEntries.detail.amount"),
      value: formatMoneyKzt(entry.amount),
      icon: WalletMoney,
    },
    {
      key: "description",
      label: t("journalEntries.detail.description"),
      value: descriptionText || "",
      icon: DocumentText1,
    },
    {
      key: "sourceType",
      label: t("journalEntries.detail.sourceType"),
      value: entry.source_type,
      icon: DocumentText1,
      isBadge: true,
    },
    {
      key: "status",
      label: t("journalEntries.detail.status"),
      value: entry.status,
      icon: Clock,
      isStatus: true,
    },
    {
      key: "organization",
      label: t("journalEntries.detail.organization"),
      value: entry.organization_name,
      icon: Building,
    },
    {
      key: "createdAt",
      label: t("journalEntries.detail.createdAt"),
      value: formatDateForDisplay(entry.created_at, true),
      icon: Calendar,
    },
  ];

  return (
    <div className="flex flex-col gap-6 h-full min-h-0">
      <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke shrink-0">
        <h4 className="text-display-2xs content-base-primary">{t("journalEntries.detail.title")}</h4>
      </div>

      <div ref={scrollRef} className={cn("flex-1 overflow-auto min-h-0 p-1 page-scroll", hasScroll && "pr-3")}>
        <div className="flex flex-col">
          {fields
            .filter(function filterEmptyFields(field) {
              return !!field.value;
            })
            .map(function renderField(field, index, array) {
              const IconComponent = field.icon;
              return (
                <div
                  key={field.key}
                  className={cn(
                    "py-3 flex items-center gap-3",
                    index < array.length - 1 && "border-b surface-base-stroke",
                  )}>
                  <span className="content-action-brand">
                    <IconComponent size={16} color="currentColor" />
                  </span>
                  <span className="text-body-regular-md content-base-secondary flex-1 min-w-[108px]">
                    {field.label}
                  </span>
                  {field.isStatus ? (
                    <div className="flex justify-end">
                      <Badge
                        variant={statusStyle.variant}
                        color={statusStyle.color}
                        text={t(`journalEntries.status.${entry.status}` as never)}
                        icon={statusStyle.icon}
                      />
                    </div>
                  ) : field.isBadge ? (
                    <div className="flex justify-end">
                      {(() => {
                        const sourceKey = `journalEntries.sourceType.${field.value}`;
                        const translated = t(sourceKey as never);
                        const text = translated && !translated.startsWith("journalEntries.") ? translated : field.value;
                        return <Badge variant="soft" color="info" text={text} />;
                      })()}
                    </div>
                  ) : (
                    <p className="text-body-bold-md content-base-primary text-right">{field.value}</p>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

export default function JournalEntryDetailModal({ entryId, onClose }: Props) {
  const { t, i18n } = useTranslation("ReportsPage");
  const { scrollRef, hasScroll } = useScrollDetection();
  const currentLang = (
    i18n.language === "ru" || i18n.language === "kk" || i18n.language === "en" ? i18n.language : "ru"
  ) as "ru" | "kk" | "en";

  const { data: entry, isLoading, isError } = useGetJournalEntryDetailQuery(entryId);

  return (
    <ModalForm icon={DocumentText1} onClose={onClose} resize={false}>
      {isLoading ? (
        <DetailSkeleton />
      ) : isError || !entry ? (
        <div className="flex flex-col gap-6 h-full min-h-0">
          <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke shrink-0">
            <h4 className="text-display-2xs content-base-primary">{t("journalEntries.detail.title")}</h4>
          </div>
          <div className="flex-1 overflow-auto min-h-0 flex items-center justify-center">
            <p className="text-body-regular-md content-base-primary">{t("journalEntries.detail.error")}</p>
          </div>
        </div>
      ) : (
        <JournalEntryDetailContent
          entry={entry}
          currentLang={currentLang}
          scrollRef={scrollRef}
          hasScroll={hasScroll}
          t={t}
        />
      )}
    </ModalForm>
  );
}
